import React, { useContext, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemButton,
  Snackbar,
  Alert,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Restaurant as RestaurantIcon,
  Movie as MovieIcon,
  Event as EventIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { FrontendNotebook, NotebooksContext } from '../contexts/NotebooksContext';
import { inferNotebookDraftKind } from '../utils/draftNotebookKind';
import { notebookDraftHeroImageUrl } from '../utils/notebookDraftThumbnail';

interface NotebookListItemProps {
  notebook: FrontendNotebook;
  onClick: (notebook: FrontendNotebook) => void;
  divider?: boolean;
}

function draftTimestampMs(notebook: FrontendNotebook): number | null {
  const raw = notebook.draft?.updated_at;
  if (raw != null) {
    return raw < 1e12 ? raw * 1000 : raw;
  }
  if (notebook.updated_at) {
    const t = new Date(notebook.updated_at).getTime();
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

const NotebookListItem: React.FC<NotebookListItemProps> = ({
  notebook,
  onClick,
  divider = false,
}) => {
  const { changeNotebookStatus, deleteNotebook, markDraftReadyForPublishing } =
    useContext(NotebooksContext);

  const [busy, setBusy] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const kind = inferNotebookDraftKind(notebook);
  const heroThumb =
    kind === 'recipe' || kind === 'movie' || kind === 'event' || kind === 'article'
      ? notebookDraftHeroImageUrl(notebook)
      : null;
  const KindIcon =
    kind === 'recipe'
      ? RestaurantIcon
      : kind === 'movie'
        ? MovieIcon
        : kind === 'event'
          ? EventIcon
          : ArticleIcon;
  const iconColor =
    kind === 'article'
      ? '#60a5fa'
      : kind === 'recipe'
        ? '#34d399'
        : kind === 'movie'
          ? '#c084fc'
          : '#fb923c';

  const editedMs = draftTimestampMs(notebook);
  const lastEditedLabel =
    editedMs != null ? format(editedMs, 'MMM d, yyyy h:mm a') : '—';

  const showSnack = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const run = async (fn: () => Promise<boolean>, okMsg: string, errMsg: string) => {
    setBusy(true);
    try {
      const ok = await fn();
      showSnack(ok ? okMsg : errMsg, ok ? 'success' : 'error');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : errMsg;
      showSnack(msg, 'error');
    } finally {
      setBusy(false);
    }
  };

  const status = notebook.status;
  const sharedProfiles = notebook.allowed_profiles?.length ?? 0;

  return (
    <>
      <ListItemButton
        onClick={() => onClick(notebook)}
        disabled={busy}
        sx={{
          borderBottom: divider ? '1px solid #27272a' : 'none',
          bgcolor: '#0a0a0a',
          mb: 1,
          borderRadius: 1,
          py: 1.5,
          px: 2,
          border: '1px solid #27272a',
          flexDirection: 'column',
          alignItems: 'stretch',
          '&:hover': { bgcolor: '#1a1a1a' },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box display="flex" gap={1.5} minWidth={0} flex={1}>
            {heroThumb ? (
              <Box
                component="img"
                src={heroThumb}
                alt=""
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  objectFit: 'cover',
                  flexShrink: 0,
                  mt: 0.25,
                  border: '1px solid #27272a',
                }}
              />
            ) : (
              <KindIcon sx={{ fontSize: 28, color: iconColor, flexShrink: 0, mt: 0.25 }} />
            )}
            <Box minWidth={0}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#fafafa' }} noWrap>
                {notebook.name}
              </Typography>
              <Typography variant="body2" noWrap sx={{ color: '#a1a1aa' }}>
                {notebook.description || 'No description'}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Edit">
              <span>
                <IconButton
                  size="small"
                  disabled={busy}
                  onClick={() => onClick(notebook)}
                  sx={{ bgcolor: '#27272a', color: '#fafafa', '&:hover': { bgcolor: '#3f3f46' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            {status === 'active' && (
              <Tooltip
                title={
                  notebook.draft?.ready_for_publishing
                    ? 'Ready to publish — locked'
                    : 'Mark ready to publish (cannot be undone)'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={busy || Boolean(notebook.draft?.ready_for_publishing)}
                    onClick={() =>
                      run(
                        () => markDraftReadyForPublishing(notebook.id, true),
                        'Draft marked ready to publish.',
                        'Could not update ready to publish.'
                      )
                    }
                    sx={{
                      bgcolor: notebook.draft?.ready_for_publishing ? '#d1fae5' : '#27272a',
                      color: notebook.draft?.ready_for_publishing ? '#047857' : '#a1a1aa',
                      '&:hover': {
                        bgcolor: notebook.draft?.ready_for_publishing ? '#d1fae5' : '#3f3f46',
                      },
                    }}
                  >
                    {notebook.draft?.ready_for_publishing ? (
                      <CheckCircleIcon fontSize="small" />
                    ) : (
                      <RadioButtonUncheckedIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {status === 'active' && (
              <Tooltip title="Archive">
                <span>
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() =>
                      run(
                        () => changeNotebookStatus(notebook.id, 'archived'),
                        `"${notebook.name}" archived.`,
                        'Failed to archive.'
                      )
                    }
                    sx={{ bgcolor: '#27272a', color: '#a1a1aa', '&:hover': { bgcolor: '#3f3f46', color: '#fafafa' } }}
                  >
                    <ArchiveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {status === 'archived' && (
              <Tooltip title="Restore from archive">
                <span>
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() =>
                      run(
                        () => changeNotebookStatus(notebook.id, 'active'),
                        `"${notebook.name}" restored to active.`,
                        'Failed to restore.'
                      )
                    }
                    sx={{ bgcolor: '#27272a', color: '#60a5fa', '&:hover': { bgcolor: '#3f3f46' } }}
                  >
                    <UnarchiveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {status !== 'trash' && (
              <Tooltip title="Move to trash">
                <span>
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() =>
                      run(
                        () => changeNotebookStatus(notebook.id, 'trash'),
                        `"${notebook.name}" moved to trash.`,
                        'Failed to move to trash.'
                      )
                    }
                    sx={{ bgcolor: '#27272a', color: '#f87171', '&:hover': { bgcolor: '#3f3f46', color: '#ef4444' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {status === 'trash' && (
              <>
                <Tooltip title="Restore">
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={() =>
                        run(
                          () => changeNotebookStatus(notebook.id, 'active'),
                          `"${notebook.name}" restored.`,
                          'Failed to restore.'
                        )
                      }
                      sx={{ bgcolor: '#27272a', color: '#60a5fa', '&:hover': { bgcolor: '#3f3f46' } }}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete permanently">
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={() => {
                        if (
                          !window.confirm(
                            `Permanently delete "${notebook.name}"? This cannot be undone.`
                          )
                        ) {
                          return;
                        }
                        void run(
                          () => deleteNotebook(notebook.id),
                          `"${notebook.name}" deleted permanently.`,
                          'Failed to delete.'
                        );
                      }}
                      sx={{ bgcolor: '#450a0a', color: '#fecaca', '&:hover': { bgcolor: '#7f1d1d' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.25, borderColor: '#27272a' }} />

        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="caption" sx={{ color: '#71717a' }}>
            Last edited {lastEditedLabel}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.75} justifyContent="flex-end">
            {status !== 'active' && (
              <Chip
                label={status === 'archived' ? 'Archived' : 'Trash'}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: status === 'trash' ? '#450a0a' : '#27272a',
                  color: status === 'trash' ? '#fecaca' : '#a1a1aa',
                }}
              />
            )}
            {notebook.visibility === 'public' && (
              <Chip
                label="Public"
                size="small"
                icon={<VisibilityIcon sx={{ fontSize: '14px !important' }} />}
                sx={{ height: 22, fontSize: '0.7rem', borderColor: '#4ade80', color: '#4ade80' }}
                variant="outlined"
              />
            )}
            {notebook.visibility === 'private' && (
              <Chip
                label="Private"
                size="small"
                icon={<LockIcon sx={{ fontSize: '14px !important' }} />}
                sx={{ height: 22, fontSize: '0.7rem', borderColor: '#a1a1aa', color: '#e4e4e7' }}
                variant="outlined"
              />
            )}
            {notebook.visibility === 'restricted' && (
              <Chip
                label="Restricted"
                size="small"
                sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#422006', color: '#fdba74' }}
              />
            )}
            {sharedProfiles > 0 && (
              <Chip
                label={sharedProfiles === 1 ? 'Shared (1 profile)' : `Shared (${sharedProfiles})`}
                size="small"
                icon={<GroupIcon sx={{ fontSize: '14px !important' }} />}
                sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#1e3a5f', color: '#bfdbfe' }}
              />
            )}
            {notebook.draft?.ready_for_publishing && (
              <Chip label="Ready" size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#14532d', color: '#bbf7d0' }} />
            )}
            {notebook.draft?.publication_status === 'scheduled' && (
              <Chip label="Scheduled" size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#422006', color: '#fde68a' }} />
            )}
          </Box>
        </Box>
      </ListItemButton>

      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotebookListItem;
